import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Workspaces } from '@air/api';
import { type WorkspaceListResponse } from '@air/api/types';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isWorkspaceKey } from '~/swr-hooks/workspaces/useWorkspace';
import { getWorkspacesListKey, isWorkspacesListKey } from '~/swr-hooks/workspaces/useWorkspaces';

export const useWorkspacesSwitchWorkspacseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof Workspaces.switchWorkspaces>[0]) => {
      /**
       * @TODO - Follow up and confirm that errors here are properly handled.
       * @see https://linear.app/air/issue/ENG-141/investigate-workspacesswitchworkspaces-not-being-able-to-catch-errors
       */
      return callAirApi(() => Workspaces.switchWorkspaces(params));
    },
    onMutate: (variables) => {
      const previousWorkspaces = queryClient.getQueryData<WorkspaceListResponse>(getWorkspacesListKey());

      if (previousWorkspaces?.length) {
        queryClient.setQueryData(getWorkspacesListKey(), (data?: WorkspaceListResponse) => {
          return data
            ? data.map((workspace) =>
                workspace.id === variables.workspaceId
                  ? {
                      ...workspace,
                      lastUsed: new Date().toISOString(),
                    }
                  : workspace,
              )
            : [];
        });
      }

      return {
        previousWorkspaces,
      };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => isWorkspacesListKey(query.queryKey),
      });
      queryClient.invalidateQueries({
        predicate: (query) => isWorkspaceKey(query.queryKey) && query.queryKey[1].id === variables.workspaceId,
      });
    },
    onError: (error, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => isWorkspaceKey(query.queryKey),
      });
      queryClient.invalidateQueries({
        predicate: (query) => isWorkspacesListKey(query.queryKey),
      });

      reportErrorToBugsnag({
        error,
        context: 'Failed to switch workspace',
        metadata: {
          key: 'data',
          data: { ...variables },
        },
      });
    },
  });
};
